/**
 * Utility functions to transform raw database data into the format expected by PDF components
 */

/**
 * Transform raw sheet data from the database into the format expected by ServiceSheetPdf
 */
export const transformServiceSheetData = (rawData: any, sheetType: 'service' | 'customApplication' = 'service') => {
    if (!rawData) {
        throw new Error('No data provided for PDF generation');
    }

    // Extract base sheet information
    const baseSheet = {
        id: rawData.id || '',
        customerName: rawData.customer || '',
        locationName: rawData.location_name || rawData.locationName || '',
        requestedBy: rawData.request_by || rawData.requestBy || rawData.requestedBy || '',
        dateServiced: rawData.dateServiced || rawData.dateserviced || new Date().toISOString(),
        po: rawData.po || '',
        total: rawData.total || 0,
        creator: rawData.creator || '',
        company: rawData.company || '',
        dept: rawData.dept || '',
        dateCreated: rawData.dateCreated || rawData.datecreated || new Date().toISOString(),
    };

    // Extract service sheet specific information
    const serviceSheet = {
        serviceType: rawData.service_type || rawData.servicetype || rawData.serviceType || '',
        serviceRequested: rawData.service_requested || rawData.servicerequested || rawData.serviceRequested || '',
        workPerformed: rawData.work_performed || rawData.workperformed || rawData.workPerformed || '',
        adminNotes: rawData.admin_notes || rawData.adminnotes || rawData.adminNotes || '',
        stage: rawData.stage || 'Archive',
    };

    // Extract parts data
    let parts: any[] = [];
    try {
        if (rawData.parts) {
            if (typeof rawData.parts === 'string') {
                parts = JSON.parse(rawData.parts);
            } else if (Array.isArray(rawData.parts)) {
                parts = rawData.parts;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse parts data', e);
        parts = [];
    }

    // Extract technician data
    let techs: any[] = [];
    try {
        if (rawData.techs) {
            if (typeof rawData.techs === 'string') {
                techs = JSON.parse(rawData.techs);
            } else if (Array.isArray(rawData.techs)) {
                techs = rawData.techs;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse techs data', e);
        techs = [];
    }

    return {
        baseSheet,
        serviceSheet,
        parts: parts || [],
        techs: techs || [],
    };
};

/**
 * Determine the sheet type for PDF generation based on the sheetType key
 */
export const getSheetTypeForPdf = (sheetTypeKey: string): 'service' | 'customApplication' => {
    if (sheetTypeKey === 'chemAppService') {
        return 'customApplication';
    }
    return 'service';
};

/**
 * Generate a filename for the PDF download
 */
export const generatePdfFilename = (sheetType: string, data: any): string => {
    const customer = data.customer || 'Unknown';
    const location = data.location_name || data.locationName || 'Unknown';
    const dateServiced = data.dateServiced || data.dateserviced || new Date().toISOString();
    const date = new Date(dateServiced).toISOString().split('T')[0]; // YYYY-MM-DD
    const id = data.id || 'unknown';
    
    // Clean up the strings to make them filename-safe
    const cleanCustomer = customer.replace(/[^a-zA-Z0-9]/g, '_');
    const cleanLocation = location.replace(/[^a-zA-Z0-9]/g, '_');
    
    const sheetTypeMap: { [key: string]: string } = {
        chemAppService: 'CustomAppService',
        sheets: 'ServiceSheet',
        preseason: 'PreSeason',
        refrigeration: 'Refrigeration',
        salesOrders: 'SalesOrder',
        inspections: 'Inspection',
        chemAppsInspections: 'CustomAppInspection',
    };
    
    const cleanSheetType = sheetTypeMap[sheetType] || sheetType;
    
    return `${cleanSheetType}_${cleanCustomer}_${cleanLocation}_${date}_${id}.pdf`;
};

/**
 * Transform raw data for Refrigeration PDF
 */
export const transformRefrigerationData = (rawData: any) => {
    if (!rawData) {
        throw new Error('No data provided for PDF generation');
    }

    const baseSheet = {
        id: rawData.id || '',
        customerName: rawData.customer || '',
        locationName: rawData.location_name || rawData.locationName || '',
        requestedBy: rawData.request_by || rawData.requestBy || '',
        dateServiced: rawData.dateServiced || rawData.dateserviced || new Date().toISOString(),
        po: rawData.po || '',
        total: rawData.total || 0,
        creator: rawData.creator || '',
        company: rawData.company || '',
        dept: rawData.dept || '',
        dateCreated: rawData.dateCreated || rawData.datecreated || new Date().toISOString(),
    };

    const refForm = {
        vacuum: rawData.vacuum || false,
        crankHeater: rawData.crankheater || rawData.crankHeater || false,
        doors: rawData.doors || false,
        contactors: rawData.contactors || false,
        notes: rawData.notes || '',
        followUp: rawData.followup || rawData.followUp || false,
        control: rawData.control || false,
        circuit: rawData.circuit || false,
        followUpDept: rawData.followupdept || rawData.followUpDept || '',
        stage: rawData.stage || 'Archive',
    };

    let compressors: any[] = [];
    try {
        if (rawData.compressors) {
            if (typeof rawData.compressors === 'string') {
                compressors = JSON.parse(rawData.compressors);
            } else if (Array.isArray(rawData.compressors)) {
                compressors = rawData.compressors;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse compressors data', e);
        compressors = [];
    }

    let parts: any[] = [];
    try {
        if (rawData.parts) {
            if (typeof rawData.parts === 'string') {
                parts = JSON.parse(rawData.parts);
            } else if (Array.isArray(rawData.parts)) {
                parts = rawData.parts;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse parts data', e);
        parts = [];
    }

    let techs: any[] = [];
    try {
        if (rawData.techs) {
            if (typeof rawData.techs === 'string') {
                techs = JSON.parse(rawData.techs);
            } else if (Array.isArray(rawData.techs)) {
                techs = rawData.techs;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse techs data', e);
        techs = [];
    }

    return {
        baseSheet,
        refForm,
        compressors,
        parts,
        techs,
    };
};

/**
 * Transform raw data for PreSeason PDF
 */
export const transformPreSeasonData = (rawData: any) => {
    if (!rawData) {
        throw new Error('No data provided for PDF generation');
    }

    const baseSheet = {
        id: rawData.id || '',
        customerName: rawData.customer || '',
        locationName: rawData.location_name || rawData.locationName || '',
        requestedBy: rawData.request_by || rawData.requestBy || '',
        dateServiced: rawData.dateServiced || rawData.dateserviced || new Date().toISOString(),
        po: rawData.po || '',
        total: rawData.total || 0,
        creator: rawData.creator || '',
        company: rawData.company || '',
        dept: rawData.dept || '',
        dateCreated: rawData.dateCreated || rawData.datecreated || new Date().toISOString(),
    };

    const preSeason = {
        ...rawData,
        stage: rawData.stage || 'Archive',
    };

    let parts: any[] = [];
    try {
        if (rawData.parts) {
            if (typeof rawData.parts === 'string') {
                parts = JSON.parse(rawData.parts);
            } else if (Array.isArray(rawData.parts)) {
                parts = rawData.parts;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse parts data', e);
        parts = [];
    }

    let techs: any[] = [];
    try {
        if (rawData.techs) {
            if (typeof rawData.techs === 'string') {
                techs = JSON.parse(rawData.techs);
            } else if (Array.isArray(rawData.techs)) {
                techs = rawData.techs;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse techs data', e);
        techs = [];
    }

    return {
        baseSheet,
        preSeason,
        parts,
        techs,
    };
};

/**
 * Transform raw data for Sales Order PDF
 */
export const transformSalesOrderData = (rawData: any) => {
    if (!rawData) {
        throw new Error('No data provided for PDF generation');
    }

    const baseSheet = {
        id: rawData.id || '',
        customerName: rawData.customer || '',
        locationName: rawData.location_name || rawData.locationName || '',
        requestedBy: rawData.request_by || rawData.requestBy || '',
        dateServiced: rawData.dateServiced || rawData.dateserviced || new Date().toISOString(),
        po: rawData.po || '',
        total: rawData.total || 0,
        creator: rawData.creator || '',
        company: rawData.company || '',
        dept: rawData.dept || '',
        dateCreated: rawData.dateCreated || rawData.datecreated || new Date().toISOString(),
    };

    const sales = {
        ...rawData,
        stage: rawData.stage || 'Archive',
    };

    let parts: any[] = [];
    try {
        if (rawData.parts) {
            if (typeof rawData.parts === 'string') {
                parts = JSON.parse(rawData.parts);
            } else if (Array.isArray(rawData.parts)) {
                parts = rawData.parts;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse parts data', e);
        parts = [];
    }

    let techs: any[] = [];
    try {
        if (rawData.techs) {
            if (typeof rawData.techs === 'string') {
                techs = JSON.parse(rawData.techs);
            } else if (Array.isArray(rawData.techs)) {
                techs = rawData.techs;
            }
        }
    } catch (e) {
        console.warn('[PDF] Failed to parse techs data', e);
        techs = [];
    }

    return {
        baseSheet,
        sales,
        parts,
        techs,
    };
};

/**
 * Transform raw data for Inspection PDF
 */
export const transformInspectionData = (rawData: any) => {
    if (!rawData) {
        throw new Error('No data provided for PDF generation');
    }

    const baseSheet = {
        id: rawData.id || '',
        customerName: rawData.customer || '',
        locationName: rawData.location_name || rawData.locationName || '',
        company: rawData.company || '',
        dateServiced: rawData.dateServiced || rawData.dateserviced || new Date().toISOString(),
    };

    const inspectionSheet = {
        ...rawData,
    };

    // Normalize bays data coming from various potential shapes
    const rawBays = rawData.bays
    let bays: any[] = [];
    try {
        if (typeof rawBays === 'string') {
            bays = JSON.parse(rawBays);
        } else if (Array.isArray(rawBays)) {
            bays = rawBays;
        } else if (rawBays && typeof rawBays === 'object' && Array.isArray(rawBays.data)) {
            bays = rawBays.data;
        }
    } catch (error) {
        console.warn('[PDF] Failed to parse bays data for inspection PDF:', error);
        bays = [];
    }

    // Normalize technician data so PDF rendering logic always receives an array
    let techs: any[] = [];
    try {
        if (typeof rawData.techs === 'string') {
            techs = JSON.parse(rawData.techs);
        } else if (Array.isArray(rawData.techs)) {
            techs = rawData.techs;
        }
    } catch (error) {
        console.warn('[PDF] Failed to parse technician data for inspection PDF:', error);
        techs = [];
    }

    return {
        baseSheet,
        inspectionSheet,
        bays,
        techs,
        dept: rawData.dept || '',
    };
};
